import { toast } from 'sonner';
import { useToastPosition } from '@/app/providers/ToastProvider.tsx';

interface ToastOptions {
  className?: string;
  style?: React.CSSProperties;
  duration?: number;
  showIcon?: boolean;
  showCloseButton?: boolean;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function useToast() {
  const { setPosition, position: currentToastPosition } = useToastPosition();

  const createToast = (
    type: 'success' | 'error' | 'info' | 'warning' | 'default',
    message: string,
    options?: ToastOptions
  ) => {
    const toastOptions = {
      className: options?.className || 'custom-toast',
      style: {
        background: 'var(--popover)',
        color: 'var(--popover-foreground)',
        border: '1px solid var(--border)',
        borderRadius: '0.8rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        ...options?.style,
      },
      duration: options?.duration || 4000,
      description: options?.description,
      action: options?.action,
      classNames: {
        toast: 'custom-toast-wrapper',
        title: 'custom-toast-title',
        description: 'custom-toast-description',
        actionButton: 'custom-toast-action',
        cancelButton: 'custom-toast-cancel',
        closeButton: 'custom-toast-close',
      },
    };

    switch (type) {
      case 'success':
        return toast.success(message, toastOptions);
      case 'error':
        return toast.error(message, toastOptions);
      case 'info':
        return toast.info(message, toastOptions);
      case 'warning':
        return toast.warning(message, toastOptions);
      default:
        return toast(message, toastOptions);
    }
  };

  return {
    toast: {
      success: (message: string, options?: ToastOptions) =>
        createToast('success', message, options),
      error: (message: string, options?: ToastOptions) =>
        createToast('error', message, options),
      info: (message: string, options?: ToastOptions) =>
        createToast('info', message, options),
      warning: (message: string, options?: ToastOptions) =>
        createToast('warning', message, options),
      default: (message: string, options?: ToastOptions) =>
        createToast('default', message, options),
      dismiss: () => toast.dismiss(),
    },
    setPosition,
    currentToastPosition,
  };
}
